import { get, isEqualWith, isPlainObject } from 'lodash';
import dayjs from 'dayjs';

/**
 * The same date travels through a wizard as a Date on one side and as a dayjs on
 * the other, depending on which step last touched it. Comparing them by their
 * instant keeps that from reading as a change.
 */
const instantOf = (value: unknown): number | undefined => {
    if (value instanceof Date) {
        return value.getTime();
    }

    return dayjs.isDayjs(value) ? value.valueOf() : undefined;
};

/** The keys that carry a value; a key set to undefined carries none. */
const definedKeys = (value: object): string[] => Object.keys(value).filter(key => undefined !== get(value, key));

export const compareValues = (left: unknown, right: unknown): boolean | undefined => {
    const leftInstant = instantOf(left);
    const rightInstant = instantOf(right);

    if (leftInstant !== undefined || rightInstant !== undefined) {
        return leftInstant === rightInstant;
    }

    // To a form, a key that is there but undefined and a key that is not there
    // are the same absence. Lodash counts the keys before it asks this function
    // anything, so the objects are compared on the keys that carry a value —
    // otherwise a mask that parked `{ ...undefined }` somewhere reported unsaved
    // input for the rest of the session.
    if (isPlainObject(left) && isPlainObject(right)) {
        const keys = new Set([...definedKeys(left as object), ...definedKeys(right as object)]);

        return [...keys].every(key => isEqualWith(get(left, key), get(right, key), compareValues));
    }

    return undefined;
};

/**
 * The names of the values that differ: top level, and inside a list per entry
 * and per field of that entry.
 *
 * Sending names upward rather than the values themselves is what keeps typing
 * cheap: the form state changes on every keystroke, but the *set of changed
 * fields* changes only on the first keystroke in a field. Carrying the values
 * up instead cost about 65ms per keystroke on the purchase order mask, measured
 * against the same mask without it.
 */
export const dirtyFieldNames = (current: unknown, baseline: unknown): string[] => {
    if (null === current || 'object' !== typeof current) {
        return [];
    }

    // Both sides' keys, not just the form's: a value the server has and the
    // form does not is a difference too, and listing only the form's keys let
    // the mark and the names disagree — the mark said something was unsaved
    // and the names said nothing was.
    const names = new Set([...Object.keys(current), ...Object.keys((baseline as object) ?? {})]);

    return [...names].flatMap(name => changedPaths(get(current, name), get(baseline, name), name)).sort();
};

/** An entry of a list that is compared field by field: not a date, not a list. */
const isRecord = (value: unknown): value is object =>
    null !== value &&
    'object' === typeof value &&
    !Array.isArray(value) &&
    undefined === instantOf(value) &&
    !(value instanceof Blob);

const isBlank = (value: unknown): boolean =>
    undefined === value || null === value || '' === value || (Array.isArray(value) && 0 === value.length);

/**
 * Where a value differs from its baseline, as field names.
 *
 * A list is compared entry by entry: taken as a whole, a customer card added
 * to an inquiry marked every field of every card as changed, the untouched
 * ones included (R-202). A list whose length changed names `<list>.length`,
 * which no field binds — the mask is unsaved, no field is marked for it. An
 * entry that is new names only the fields that carry something.
 */
const changedPaths = (current: unknown, baseline: unknown, path: string, added = false): string[] => {
    if (Array.isArray(current) && (Array.isArray(baseline) || isBlank(baseline))) {
        const before: unknown[] = Array.isArray(baseline) ? baseline : [];
        const paths = current.length === before.length ? [] : [`${path}.length`];

        for (let index = 0; index < Math.max(current.length, before.length); index++) {
            const entry = `${path}.${index}`;

            if (index >= current.length) {
                paths.push(entry);
            } else if (added || index >= before.length) {
                paths.push(...addedPaths(current[index], entry));
            } else if (isRecord(current[index]) && isRecord(before[index])) {
                const keys = new Set([
                    ...Object.keys(current[index] as object),
                    ...Object.keys(before[index] as object),
                ]);

                keys.forEach(key =>
                    paths.push(...changedPaths(get(current[index], key), get(before[index], key), `${entry}.${key}`))
                );
            } else {
                paths.push(...changedPaths(current[index], before[index], entry));
            }
        }

        return paths;
    }

    if (added) {
        return addedPaths(current, path);
    }

    return isEqualWith(current, baseline, compareValues) ? [] : [path];
};

/** The fields of a new list entry that carry something. */
const addedPaths = (current: unknown, path: string): string[] => {
    if (Array.isArray(current)) {
        return changedPaths(current, [], path, true);
    }

    if (isRecord(current)) {
        return Object.keys(current).flatMap(key =>
            Array.isArray(get(current, key))
                ? changedPaths(get(current, key), [], `${path}.${key}`, true)
                : isBlank(get(current, key))
                ? []
                : [`${path}.${key}`]
        );
    }

    return isBlank(current) ? [] : [path];
};
